import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { appPath } from "@/constants/paths";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/shared/loader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authService } from "@/features/auth/services/authService";
import { useAuthStore, useUser } from "@/features/auth/store";
import { authCookies } from "@/lib/cookies";
import { getErrorMessage } from "@/lib/error-handler";
import { cn } from "@/lib/utils";
import { AlertCircle } from "lucide-react";
import { toast } from "sonner";

const schema = z
  .object({
    newPassword: z
      .string()
      .min(8, { message: "كلمة المرور يجب أن تكون 8 أحرف على الأقل" }),
    confirmPassword: z.string().min(1, { message: "تأكيد كلمة المرور مطلوب" }),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "كلمتا المرور غير متطابقتين",
    path: ["confirmPassword"],
  });

type FormValues = z.infer<typeof schema>;

export default function RequiredPasswordChangeForm() {
  const navigate = useNavigate();
  const user = useUser();
  const setUser = useAuthStore((s) => s.setUser);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: { newPassword: "", confirmPassword: "" },
  });

  const onSubmit = async (data: FormValues) => {
    setErrorMessage(null);
    try {
      const updated = await authService.completeInitialPassword(data.newPassword);
      setUser(updated);
      authCookies.setUser(updated);
      toast.success("تم تعيين كلمة المرور بنجاح");
      navigate(appPath(), { replace: true });
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={cn(
        "flex flex-col bg-card p-4 rounded-lg shadow w-full max-w-md",
      )}
    >
      <div className="mb-4 space-y-1 text-center">
        <h2 className="text-lg font-semibold text-foreground">
          تعيين كلمة المرور
        </h2>
        <p className="text-sm text-muted-foreground">
          {user?.firstName
            ? `مرحباً ${user.firstName} — يجب تعيين كلمة مرور جديدة قبل المتابعة.`
            : "يجب تعيين كلمة مرور جديدة قبل المتابعة."}
        </p>
      </div>

      {errorMessage && (
        <div className="flex items-start gap-2 mb-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="newPassword">كلمة المرور الجديدة</Label>
          <Input
            id="newPassword"
            type="password"
            autoComplete="new-password"
            {...register("newPassword")}
          />
          {errors.newPassword && (
            <p className="text-sm text-destructive">{errors.newPassword.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword">تأكيد كلمة المرور</Label>
          <Input
            id="confirmPassword"
            type="password"
            autoComplete="new-password"
            {...register("confirmPassword")}
          />
          {errors.confirmPassword && (
            <p className="text-sm text-destructive">
              {errors.confirmPassword.message}
            </p>
          )}
        </div>
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full h-10 mt-4 text-white font-medium"
      >
        <LoadingButton
          loading={isSubmitting}
          loadingText="جار الحفظ..."
          loaderSize="sm"
        >
          حفظ والمتابعة
        </LoadingButton>
      </Button>
    </form>
  );
}
